import { Box, Divider, Grid, Switch, TextField } from "@mui/material";
import { calcOldPrimitive } from "common/functions/estates";
import { squareToTsubo } from "common/functions/utilities";
import { onAutocompleteChange } from "common/interfaces/AutocompleteHandlers";
import { JsonMaster } from "common/interfaces/JsonMaster";
import { StructureForPricingAutocomplete } from "components/Parts/Atoms/StructureForPricingAutocomplete";
import React from "react";
import styles from "../PricingDialog.module.scss";

export const HouseCalculate = React.memo(
  (props: {
    remainYears: number | undefined;
    remainRate: number | undefined;
    structure: string | null | undefined;
    usefulLifeYears: number | undefined;
    year: number | undefined;
    buildPriceOfTsubo: number | undefined;
    forceRemainRateTo10: boolean;
    squareArea: number | undefined;
    houseAmount: number;
    onStructureChange: onAutocompleteChange<string | JsonMaster>;
    onStructureBlur: () => Promise<void>;
    onUsefulLifeYearsChange: (
      event: React.ChangeEvent<HTMLInputElement>
    ) => void;
    onRemainRateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onYearChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onYearBlur: () => Promise<void>;
    onBuildPriceChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onForceRemainRateTo10Change: (
      event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean
    ) => void;
    onSquareAreaChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }) => {
    const old = props.year
      ? calcOldPrimitive(props.year * 100 + 1, new Date())
      : undefined;
    const tsuboArea = props.squareArea ? squareToTsubo(props.squareArea) : 0;

    return (
      <Box className={styles.calculate}>
        <Box className={styles.category}>建物</Box>
        <Grid container columnSpacing={3} rowSpacing={1} className={styles.row}>
          <Grid
            className={styles.column}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            構造：
            <StructureForPricingAutocomplete
              className={styles.structures}
              disablePortal
              value={props.structure}
              slotProps={{
                paper: {
                  className: styles.noBorder
                },
                popper: { className: styles.noBorder }
              }}
              onChange={props.onStructureChange}
              onBlur={props.onStructureBlur}
            />
          </Grid>
          <Grid
            className={styles.column}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            建築年（西暦）：
            <TextField
              className={styles.yearInput}
              value={props.year || ""}
              type="number"
              slotProps={{
                input: {
                  classes: { notchedOutline: styles.noBorder }
                }
              }}
              onChange={props.onYearChange}
              onBlur={props.onYearBlur}
            />
            年 （築 <Box className={styles.old}>{old || ""}</Box> 年）
          </Grid>
          <Grid
            className={styles.column}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            耐用年数：
            <TextField
              className={styles.usefulLifeYearsInput}
              value={props.usefulLifeYears || ""}
              type="number"
              slotProps={{
                input: {
                  classes: { notchedOutline: styles.noBorder }
                }
              }}
              onChange={props.onUsefulLifeYearsChange}
            />
            年 （耐用年数残：
            <Box className={styles.remainYears}>{props.remainYears}</Box> 年）
          </Grid>
        </Grid>
        <Grid container columnSpacing={3} rowSpacing={1} className={styles.row}>
          <Grid size={{ xs: 12 }}>
            <Divider className={styles.divider} />
          </Grid>
        </Grid>
        <Grid container columnSpacing={3} rowSpacing={1} className={styles.row}>
          <Grid
            className={styles.column}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            再建築単価：
            <TextField
              className={styles.buildPriceInput}
              value={props.buildPriceOfTsubo || ""}
              type="number"
              slotProps={{
                input: {
                  classes: { notchedOutline: styles.noBorder }
                }
              }}
              onChange={props.onBuildPriceChange}
            />
            円／坪
          </Grid>
          <Grid
            className={`${styles.column} ${styles.operator}`}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            ×
          </Grid>
          <Grid
            className={styles.column}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            残価率：
            <TextField
              className={`${styles.remainRateInput} ${
                props.forceRemainRateTo10 ? styles.disabled : ""
              }`}
              disabled={props.forceRemainRateTo10}
              value={props.remainRate || ""}
              type="number"
              slotProps={{
                input: {
                  classes: { notchedOutline: styles.noBorder }
                }
              }}
              onChange={props.onRemainRateChange}
            />
            % 【残価10%：
            <Switch
              checked={props.forceRemainRateTo10}
              onChange={props.onForceRemainRateTo10Change}
            />
            】
          </Grid>
          <Grid
            className={`${styles.column} ${styles.operator}`}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            ×
          </Grid>
          <Grid
            className={styles.column}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            延床面積：
            <TextField
              className={styles.squareAreaInput}
              value={props.squareArea || ""}
              type="number"
              slotProps={{
                input: {
                  classes: { notchedOutline: styles.noBorder }
                }
              }}
              onChange={props.onSquareAreaChange}
            />
            ㎡ （<Box className={styles.tsuboArea}>{tsuboArea || 0}</Box> 坪）
          </Grid>
          <Grid
            className={styles.column}
            size={{
              sm: 12,
              md: "grow"
            }}
          ></Grid>
          <Grid
            className={`${styles.column} ${styles.operator}`}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            =
          </Grid>
          <Grid
            className={`${styles.column}`}
            size={{
              sm: 12,
              md: "auto"
            }}
          >
            <span className={styles.amount}>{props.houseAmount}</span>
            万円
          </Grid>
        </Grid>
        <Grid container columnSpacing={3} rowSpacing={1} className={styles.row}>
          <Grid size={{ xs: 12 }}>
            <Divider className={styles.divider} />
          </Grid>
        </Grid>
        <Grid container columnSpacing={3} rowSpacing={1} className={styles.row}>
          <Grid size={{ xs: 12 }}>
            <Box className={styles.note}>
              ※国土交通省：｢建築着工統計｣の｢構造別:建築物の数､床面積の合計､工事費予定額｣表を基に、1坪当たりの再建築単価を算出（工事費予定額÷床面積の合計）
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
);
